import React, {useState} from 'react';
import {Languages, Copy, Loader2, AlertTriangle} from 'lucide-react';

const TranslationPage = () => {
    const [inputText, setInputText] = useState('');
    const [sourceLanguage, setSourceLanguage] = useState('en');
    const [translatedText, setTranslatedText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    const handleTranslation = async () => {
        if (!inputText.trim()) {
            setError('Please enter text to translate');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            // Constructing the query string
            const queryString = new URLSearchParams({
                text: inputText,
                source_language: sourceLanguage
            }).toString();

            const response = await fetch(`https://api.digitalaalim.com/translate-urdu/?${queryString}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Translation failed with status: ${response.status}`);
            }

            const data = await response.json();
            setTranslatedText(data.translated_text);
        } catch (error) {
            console.error('Error during translation', error);
            setError(error.message || 'An error occurred during translation');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopyText = () => {
        navigator.clipboard.writeText(translatedText)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(err => {
                console.error('Failed to copy text: ', err);
            });
    };

    const languageOptions = [
        {value: 'en', label: 'English'},
        {value: 'ar', label: 'Arabic'},
        {value: 'fa', label: 'Persian (Farsi)'}
    ];

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <Languages className="text-blue-600" size={32}/>
                    Digital Aalim Language Translator
                </h1>
                <p className="mt-2 text-gray-600">
                    Translate from English, Arabic, and Persian (Farsi) to Urdu using AI assistance
                </p>
            </div>

            {/* Main content */}
            <div className="grid md:grid-cols-2 gap-6">
                {/* Source text section */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-semibold text-gray-800">Source Text</h2>

                        <div className="flex items-center space-x-2">
                            <label htmlFor="language" className="text-sm text-gray-600">
                                Source Language:
                            </label>
                            <select
                                id="language"
                                value={sourceLanguage}
                                onChange={(e) => setSourceLanguage(e.target.value)}
                                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {languageOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <textarea
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        placeholder={`Enter text to translate from ${
                            languageOptions.find(opt => opt.value === sourceLanguage)?.label
                        } to Urdu...`}
                        className="w-full h-64 p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />

                    <button
                        onClick={handleTranslation}
                        disabled={isLoading || !inputText.trim()}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                            isLoading || !inputText.trim()
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-blue-600 text-white hover:bg-blue-700"
                        }`}
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="animate-spin" size={16}/>
                                Translating...
                            </>
                        ) : (
                            'Translate to Urdu'
                        )}
                    </button>
                </div>

                {/* Translated text section */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-semibold text-gray-800">Translated Urdu Text</h2>

                        {translatedText && (
                            <button
                                onClick={handleCopyText}
                                className="flex  items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                            >
                                <Copy size={14}/>
                                {copied ? 'Copied!' : 'Copy Text'}
                            </button>
                        )}
                    </div>

                    {error && (
                        <div className="flex items-center p-3 bg-red-50 text-red-700 rounded-md">
                            <AlertTriangle size={16} className="mr-2 flex-shrink-0"/>
                            <p className="text-sm">{error}</p>
                        </div>
                    )}

                    <div
                        className={`w-full font-[Calibri] h-64 p-4 border border-gray-300 rounded-lg overflow-auto ${
                            translatedText ? 'font-urdu' : 'text-gray-400'
                        }`}
                        style={{
                            fontFamily: translatedText ? ' Calibri, sans-serif' : 'inherit',
                            fontSize: translatedText ? '1.1rem' : 'inherit',
                            direction: 'rtl'
                        }}
                    >
                        {translatedText}
                    </div>

                    {translatedText && (
                        <div className="text-right">
                            <p className="text-xs text-gray-500">
                                Translated from {
                                languageOptions.find(opt => opt.value === sourceLanguage)?.label
                            } to Urdu
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TranslationPage;